import { AlertService } from '@/common/services/alert/alert.service';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { UrlService } from '@/common/services/url/url.service';
import { sendCartStep1UseCase } from '@/core/domain/cart-step1/usecases/sendCartStep1.usecase';
import { sendCartStep2InsuranceUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Insurance.usecase';
import { sendCartStep2ProductUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Product.usecase';
import { CartStep2InsuranceInterface } from '@/core/domain/cart-step2/models/cart-step2-insurance.model';
import { CartStep2ProductInterface } from '@/core/domain/cart-step2/models/cart-step2-product.model';
import { Product } from '@/core/domain/extras/models/extras-data.model';
import { GtmSelectCartUseCase } from '@/core/domain/gtm/usecases/gtmSelectCar.usecase';
import { UpdateSummary } from '@/core/domain/update-category/models/update-summary.model';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { v4 as uuidv4 } from 'uuid';
import { SwalPortalTargets } from '@sweetalert2/ngx-sweetalert2';
import { updateReservation } from '@/core/reservation/store/reservation.actions';
import { CartStep1Interface } from '@/core/domain/cart-step1/models/cart-step1.model';
import { catchError, of, switchMap, take } from 'rxjs';
import { isValid } from '@/common/utils/validation/common.utils';
import { Vehicle } from '@/core/domain/branches/models/branch.model';
import { selectVehicles } from '@/core/reservation/store/vehicles.selector';
import { ChangeDetectionStrategy } from '@angular/core';

@Component({
    selector: 'fe-upgrade-category',
    templateUrl: './upgrade-category.component.html',
    styleUrls: ['./upgrade-category.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpgradeCategoryComponent {
    @Input() updateSummary?: UpdateSummary;
    @Input() loadingBtn?: boolean;
    @Input() loadingPrice?: boolean;

    public confirmed = false;

    constructor(
        private readonly router: Router,
        private readonly store: Store,
        public readonly swalTargets: SwalPortalTargets,
        private readonly sendCartStep1UseCase: sendCartStep1UseCase,
        private readonly sendCartStep2InsuranceUseCase: sendCartStep2InsuranceUseCase,
        private readonly sendCartStep2ProductUseCase: sendCartStep2ProductUseCase,
        public reservation: CurrentReservationService,
        private readonly url: UrlService,
        private readonly alert: AlertService,
        private readonly gtmSelectCart: GtmSelectCartUseCase,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    public update(): void {
        const uniqueId = uuidv4();
        const id = Number(this.updateSummary?.categoryUpdate?.id);
        let vehicle: Vehicle | undefined;
        let flowType: 'office' | 'prepaid' = 'office';
        this.store
            .select(selectVehicles)
            .pipe(take(1))
            .subscribe((response) => {
                vehicle = response.vehicles.find((v: Vehicle) => v.id === id);
            });

        this.store
            .select(selectReservation)
            .pipe(take(1))
            .subscribe((reservation) => {
                flowType =
                    reservation?.flowType === 'prepaid' ? 'prepaid' : 'office';
            });

        this.store.dispatch(
            updateReservation({
                value: {
                    vehicle: vehicle,
                    type: true,
                    flowType,
                    uuid: uniqueId,
                    currency: 'MXN',
                    isUpgradePromotion: false,
                },
            }),
        );

        if (vehicle) {
            this.reservation.vehicle = vehicle;
        }

        this.loadingBtn = true;
        this.cdr.markForCheck();

        this.store
            .select(selectReservation)
            .pipe(
                take(1),
                switchMap((reservation) => {
                    const cartData: CartStep1Interface = {
                        sessionId: uniqueId,
                        startDate: reservation.startDate,
                        endDate: reservation.endDate,
                        branchId: reservation.locationSelected,
                        isPrePaid: flowType === 'prepaid',
                        currency: 'MXN',
                    };

                    cartData.carId = reservation.vehicle.id;

                    if (isValid(reservation.differentLocationSelected)) {
                        cartData.returnBranchId =
                            reservation.differentLocationSelected;
                    }

                    if (reservation.discountCode) {
                        cartData.couponCode = reservation.discountCode;
                    }

                    if (reservation.age) {
                        cartData.age = reservation.age;
                    }

                    if (reservation.isUpgradePromotion) {
                        cartData.isUpgradePromotion =
                            reservation.isUpgradePromotion;
                        cartData.carIdUpgradePromotion =
                            reservation.upgradeCatergoryPromotion.id;
                    }

                    return this.sendCartStep1UseCase.execute(cartData).pipe(
                        switchMap((response) => {
                            this.store.dispatch(
                                updateReservation({
                                    value: {
                                        total: response.data?.amount,
                                    },
                                }),
                            );

                            // Si hay seguros seleccionados, enviarlos
                            if (
                                reservation.insurances &&
                                reservation.insurances.length > 0
                            ) {
                                const insuranceData: CartStep2InsuranceInterface =
                                    {
                                        sessionId: uniqueId,
                                        insuranceId:
                                            reservation.insurances[0].id,
                                        isPrePaid: flowType === 'prepaid',
                                    };

                                return this.sendCartStep2InsuranceUseCase
                                    .execute(insuranceData)
                                    .pipe(
                                        switchMap((insuranceResponse) => {
                                            this.store.dispatch(
                                                updateReservation({
                                                    value: {
                                                        total: insuranceResponse
                                                            .data?.amount,
                                                    },
                                                }),
                                            );

                                            // Si hay productos seleccionados, enviarlos
                                            if (
                                                reservation.products &&
                                                reservation.products.length > 0
                                            ) {
                                                const productIds =
                                                    reservation.products.map(
                                                        (p: Product) => p.id,
                                                    );

                                                const extras =
                                                    reservation.products.map(
                                                        (product: any) => [
                                                            {
                                                                name: product.name,
                                                                value: product.price,
                                                                quantity: 1,
                                                            },
                                                        ],
                                                    );

                                                const productData: CartStep2ProductInterface =
                                                    {
                                                        sessionId: uniqueId,
                                                        products: productIds,
                                                        isPrePaid:
                                                            flowType ===
                                                            'prepaid',
                                                    };

                                                const additionalDriverIndex =
                                                    reservation.products.findIndex(
                                                        (product: Product) =>
                                                            product.id === 0,
                                                    );

                                                if (
                                                    additionalDriverIndex !== -1
                                                ) {
                                                    productData.extras =
                                                        extras[
                                                            additionalDriverIndex
                                                        ];
                                                }

                                                return this.sendCartStep2ProductUseCase
                                                    .execute(productData)
                                                    .pipe(
                                                        switchMap(
                                                            (
                                                                productResponse,
                                                            ) => {
                                                                this.store.dispatch(
                                                                    updateReservation(
                                                                        {
                                                                            value: {
                                                                                total: productResponse
                                                                                    .data
                                                                                    ?.amount,
                                                                            },
                                                                        },
                                                                    ),
                                                                );
                                                                return of(true);
                                                            },
                                                        ),
                                                    );
                                            }

                                            return of(true);
                                        }),
                                    );
                            }

                            return of(true);
                        }),
                        catchError((error) => {
                            this.alert.openDefault(error);
                            return of(null);
                        }),
                    );
                }),
            )
            .subscribe((isSuccessful) => {
                this.loadingBtn = false;

                if (isSuccessful) {
                    this.reservation.current.sessionId = uniqueId;
                    // this.reservation.total = this.updateSummary?.total;
                    this.confirmed = true;
                    this.gtmSelectCart.execute().subscribe();
                } else {
                    console.error('Navigation cancelled due to an error.');
                }

                this.cdr.markForCheck();
            });
    }
}
